import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material/";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import classes from "./tip-card.module.scss";
import DateHelper from "../../helpers/date.helper";
import Tags from "../../views/tags/Tags";
import { TipCardProps } from "../../interfaces/props/TipCardProps";
import { useDispatch, useSelector } from "react-redux";
import {
  setTipsAuthors,
  setTipsTags,
} from "../../../store/filters/tipFilters.slice";
import { RootState } from "../../../store";
import { Tag } from "../../interfaces/Tag";
import AuthorInfoPopup from "../../views/authorPopup/AuthorPopup";
import TipLength from "../tip-length/TipLength";

const TipCard = ({ id, attributes }: TipCardProps) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showRawAnalysis, setShowRawAnalysis] = useState<boolean>(false);
  const [showAuthorInfo, setShowAuthorInfo] = useState<boolean>(false);
  const navigate = useNavigate();
  const currentTags = useSelector(
    (state: RootState) => state.tipFilters.tipFilters.tags || []
  );
  const currentAuthors = useSelector(
    (state: RootState) => state.tipFilters.tipFilters.authors || []
  );

  const calculateTimeDifference: string = useMemo(() => {
    return DateHelper.calculateTimeDifferenceFromNow(
      dayjs(attributes.postCreatedAt ?? attributes.createdAt)
    );
  }, [attributes]);

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setIsExpanded(newExpanded);
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowRawAnalysis(event.target.checked);
  };

  const handleTagClick = useCallback(
    (tag: Tag) => {
      const isTagAlreadySelected = currentTags.some(
        (existingTag) => existingTag.id === tag.id
      );

      if (!isTagAlreadySelected) {
        const updatedTags = [...currentTags, tag];
        dispatch(setTipsTags(updatedTags));
        navigate(`/`);
      }
    },
    [dispatch, navigate, currentTags]
  );

  const handleAuthorClick = (event: React.MouseEvent) => {
    if (attributes.author.data) {
      const isAuthorAlreadySelected = currentAuthors.some(
        (existingAuthor) => existingAuthor.id === attributes?.author?.data?.id
      );

      if (!isAuthorAlreadySelected) {
        const updatedAuthors = [...currentAuthors, attributes.author.data];
        dispatch(setTipsAuthors(updatedAuthors));
        navigate(`/`);
      }
    }
  };

  return (
    <Box className={`${classes.details} d-flex flex-column p-3 p-sm-0 border`}>
      <Box className="d-flex flex-column gap-1 w-100 pt-3 px-3">
        <Box className="d-flex flex-wrap align-items-start justify-content-between w-100">
          <Box className="d-flex gap-1 flex-wrap">
            <Typography variant="h6" className={`text-headline`}>
              {attributes.englishTitle}
            </Typography>
            {attributes.betOption && (
              <Box className="d-flex align-items-center gap-2">
                {attributes.betOption?.usOdds && (
                  <Chip
                    label={`US: ${attributes.betOption.usOdds}`}
                    variant="outlined"
                    size="small"
                    color="primary"
                    className={classes.oddsChip}
                  />
                )}
                {attributes.betOption?.decimalOdds && (
                  <Chip
                    label={`Decimal: ${attributes.betOption.decimalOdds}`}
                    variant="outlined"
                    size="small"
                    color="secondary"
                    className={classes.oddsChip}
                  />
                )}
              </Box>
            )}
          </Box>
          <Typography
            variant="caption"
            className={classes.dateColor}
            whiteSpace="pre-wrap"
          >
            {calculateTimeDifference}
          </Typography>
        </Box>

        <Box className="d-flex align-items-center justify-content-between w-100">
          <Box className="d-flex flex-column gap-1">
            {attributes.author.data !== null && (
              <Box
                className="position-relative"
                style={{ alignSelf: "flex-start" }}
              >
                <Typography
                  variant="caption"
                  className={classes.authorText}
                  onMouseEnter={() => setShowAuthorInfo(true)}
                  onMouseLeave={() => setShowAuthorInfo(false)}
                  onClick={handleAuthorClick}
                >
                  By: {attributes.author.data.attributes.name}
                </Typography>
                {showAuthorInfo && (
                  <AuthorInfoPopup author={attributes.author.data} />
                )}
              </Box>
            )}
            {attributes.numberOfWords !== null && (
              <TipLength numberOfWords={attributes.numberOfWords} />
            )}
          </Box>
        </Box>

        <Link
          to={`../tip/${id}`}
          style={{ textDecoration: "none", color: "black" }}
        >
          <Typography variant="body2" className={classes.summary}>
            {attributes.summary}
          </Typography>
        </Link>
        <Box className="my-1">
          <Tags
            tags={attributes.tags?.data ?? []}
            onTagClick={(tag) => handleTagClick(tag)}
          />
        </Box>
      </Box>
      <Accordion
        expanded={isExpanded}
        onChange={handleAccordionChange}
        className={`${classes.card} d-flex flex-column justify-content-center`}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          classes={{
            content: "mx-2",
            expandIconWrapper: "d-flex justify-content-center w-100",
          }}
        ></AccordionSummary>
        <AccordionDetails
          className={`${classes.details} d-flex flex-column border p-3 w-100`}
        >
          <Box className="d-flex flex-row justify-content-end align-items-center mb-2">
            <FormControlLabel
              control={
                <Switch
                  classes={{ checked: "switch", track: "switch-base" }}
                  checked={showRawAnalysis}
                  onChange={handleToggleChange}
                  size="small"
                />
              }
              label="Show Raw Content"
              labelPlacement="start"
              className="pe-2"
            />
          </Box>
          {attributes.betOption && (
            <Box className="pb-2">
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="mb-2"
                fontSize="1rem"
              >
                Betting Information:
              </Typography>
              <Box className="d-flex flex-wrap gap-2 mb-2">
                {attributes.betOption?.betType && (
                  <Typography variant="body2" fontSize="0.9rem">
                    <strong>Bet Type:</strong> {attributes.betOption.betType}
                  </Typography>
                )}
                {attributes.betOption?.line && (
                  <Typography variant="body2" fontSize="0.9rem">
                    <strong>Line:</strong> {attributes.betOption.line}
                  </Typography>
                )}
                {attributes.betOption?.lineType && (
                  <Typography variant="body2" fontSize="0.9rem">
                    <strong>Line Type:</strong> {attributes.betOption.lineType}
                  </Typography>
                )}
                {attributes.betOption?.optionType && (
                  <Typography variant="body2" fontSize="0.9rem">
                    <strong>Option Type:</strong>{" "}
                    {attributes.betOption.optionType}
                  </Typography>
                )}

                {attributes.betOption?.periodType && (
                  <Typography variant="body2" fontSize="0.9rem">
                    <strong>Period Type:</strong>{" "}
                    {attributes.betOption.periodType}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {attributes.facts && (
            <Box className="pb-2">
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="mb-1"
                fontSize="1rem"
              >
                Facts:
              </Typography>
              <Typography
                className={`${classes.factsContent} py-2 ps-3`}
                whiteSpace="pre-wrap"
                fontFamily="inherit"
              >
                {attributes.facts}
              </Typography>
            </Box>
          )}
          {showRawAnalysis ? (
            <Box className={classes.analysisWrapper}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="mb-1"
                fontSize="1rem"
              >
                Raw Analysis:
              </Typography>
              <Typography className={`pt-2 text-base`}>
                {attributes.rawAnalysis}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="mb-1"
                fontSize="1rem"
              >
                English Analysis:
              </Typography>
              <Typography className={`pt-2 text-base`}>
                {attributes.translatedAnalysis}
              </Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TipCard;
